import { IMembershipGeneralDetailsPostBody } from "@/api_framework/api_modals/Membership";
import Input from "@/app/components/ui/Inputs/Input";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import Toggle from "@/app/components/ui/Toggles";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useInventoryActiveTaxListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryActiveTaxListAPI";
import {
  IAddMembershipCategoryProps,
  useAddMembershipCategoryAPI,
} from "@/app/hooks/api_hooks/Membership/useAddMembershipCategoryAPI";
import { useMembershipCategoryListingAPI } from "@/app/hooks/api_hooks/Membership/useMembershipCategoryListingAPI";
import { useMembershipCreateGeneralDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipCreateGeneralDetailsAPI";
import { useMembershipDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipDetailsAPI";
import { useUpdateMembershipAPI } from "@/app/hooks/api_hooks/Membership/useUpdateMembershipAPI";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useConfigurationStaffClinicBasedOnCityAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffClinicBasedOnCityAPI";
import { useMembershipVMSCategoryAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVMSCategoryAPI";
import { useCouponSubCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { IMembershipState } from "@/app_redux/reducers/slices/auth/membership.slice";
import { Switch } from "@headlessui/react";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { IoIosAddCircle } from "react-icons/io";
import { IoTrashOutline } from "react-icons/io5";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

type TaxDetailsPreferencesTypes = "taxable" | "nonTaxable";

export interface IMembershipSetupGeneralDetailsProps {
  membership_name: string;
  membership_category: string;
  membership_amount: number;
  cities: Array<{ id: string; name: string }>;
  clinics: Array<{ id: string; name: string }>;
  membership_validity: string;
  status: boolean;

  lineItems: {
    vms_category_id: string;
    vms_category_name: string;
    vms_subcategory_name: string;
    sub_category_data?: any;
    vms_subcategory_id: string;
    membership_line_item_name: string;
    membership_invoice_category: string;
    membership_invoice_amount: number;
    tax_preferences: TaxDetailsPreferencesTypes;
    hsncode: string;
    cgst: number;
    sgst: number;
    igst: number;
    ugst: number;
  }[];
}

const MembershipDetailsComponent = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const fetchCities = useConfigurationCityListAPI();
  const { cityList } = routeState?.state as IConfigurationListState;
  const { clinicsByCities, execute: fetchStaffClinicBasedOnCity } = useConfigurationStaffClinicBasedOnCityAPI();
  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id");
  const clinicsOrgItem = useSelectMenuReducer(clinicsByCities, "clinic_name", "clinic_id");
  const { routeState: taxroutestate } = useRouteInfo(getRouteKey("INVENTORY", "id"));
  const { taxList } = taxroutestate.state as IInventoryState;
  const taxListItems = useSelectMenuReducer(taxList, "hsn_code", "hsn_code");
  const fetchTaxList = useInventoryActiveTaxListAPI();
  const fetchMembershipCategoryList = useMembershipCategoryListingAPI();
  const { membershipCategoryList } = useRouteInfo("MEMBERSHIP_SETUP")?.routeState?.state as IMembershipState;
  const membershipCategoryOptions = useSelectMenuReducer(membershipCategoryList, "category_name", "id");
  const [params, _setParams] = useSearchParams();
  const membership_id = params.get("membership_id");
  const { execute: membershipGeneralDetailsCreation } = useMembershipCreateGeneralDetailsAPI();
  const [_isActive, setIsActive] = useState(false);
  const { execute: fetchAddCategory } = useAddMembershipCategoryAPI();
  const { execute: fetchMembershipDetails, membershipDetails } = useMembershipDetailsAPI();
  const { execute: updateMembership } = useUpdateMembershipAPI();
  const { forNumericWithDecimalAndNotStartFromZero } = useFormValidations();

  const { execute: fetchMembershipCategory, membershipCategory } = useMembershipVMSCategoryAPI();
  const { execute: fetchSubCategory, serviceSubCategory: couponSubCategory } = useCouponSubCategoryAPI();
  
  const formHook = useForm<IMembershipSetupGeneralDetailsProps>({
    mode: "onChange",
    defaultValues: {
      status: true,
      membership_category: null,
      lineItems: [
        {
          vms_category_id: null,
          vms_category_name: null,
          vms_subcategory_name: null,
          vms_subcategory_id: null,
          sub_category_data: [],
          membership_line_item_name: "",
          membership_invoice_category: null,
          membership_invoice_amount: null,
          tax_preferences: "taxable",
          hsncode: "",
          cgst: 0,
          sgst: 0,
          igst: 0,
          ugst: 0,
        },
      ],
    },
  });

  const { control } = formHook;
  const { fields, append, remove, update } = useFieldArray({
    name: "lineItems",
    control,
  });

  const addLineItem = () => {
    append({
      vms_category_id: null,
      vms_category_name: null,
      vms_subcategory_name: null,
      vms_subcategory_id: null,
      sub_category_data: [],
      membership_line_item_name: "",
      membership_invoice_category: "",
      membership_invoice_amount: 0,
      tax_preferences: "taxable",
      hsncode: "",
      cgst: 0,
      sgst: 0,
      igst: 0,
      ugst: 0,
    });
  };

  const deleteLineItem = (index: number) => {
    remove(index);
  };

  const handelBackStepper = () => {
    navigate(getRouteKey("MEMBERSHIP_SETUP_LIST", "url"));
  };

  useEffect(() => {
    fetchCities();
    fetchTaxList();
    fetchMembershipCategory();
    fetchMembershipCategoryList();
  }, []);

  useEffect(() => {
    if (membership_id) {
      fetchMembershipDetails(membership_id);
    }
  }, [membership_id]);

  const handelCreateItems = async (item: string) => {
    if (item) {
      const newCategory: IAddMembershipCategoryProps = {
        category_name: item,
      };
      await fetchAddCategory(newCategory).then(async () => {
        await fetchMembershipCategoryList().then(() => {
          formHook.setValue("membership_category", item);
        });
      });
    }
  };

  useEffect(() => {
    const currentLineItems = formHook.getValues("lineItems");
    currentLineItems.forEach((item, index) => {
      const hsncode = item.hsncode;
      if (hsncode) {
        const filterHsn = taxList.filter((taxItem) => taxItem.hsn_code === hsncode);
        const filterData = filterHsn[0];
        if (filterData) {
          formHook.setValue(`lineItems.${index}.cgst`, filterData.cgst_percentage);
          formHook.setValue(`lineItems.${index}.sgst`, filterData.sgst_percentage);
          formHook.setValue(`lineItems.${index}.igst`, filterData.igst_percentage);
          formHook.setValue(`lineItems.${index}.ugst`, filterData.ugst_percentage);
        }
      }
    });
  }, [formHook.watch("lineItems")]);

  useEffect(() => {
    const currentClinics = formHook.getValues("clinics") || [];
    if (currentClinics.length === 0) return;
    const validClinics = currentClinics.filter((clinic) =>
      clinicsByCities?.some((clinicByCity) => clinicByCity.clinic_id === clinic.id),
    );
    if (validClinics.length !== currentClinics.length) {
      formHook.setValue("clinics", validClinics);
    }
  }, [clinicsByCities]);

  const validityItems = [
    { title: "1 month", id: "1" },
    { title: "2 months", id: "2" },
    { title: "3 months", id: "3" },
    { title: "4 months", id: "4" },
    { title: "5 months", id: "5" },
    { title: "6 months", id: "6" },
    { title: "7 months", id: "7" },
    { title: "8 months", id: "8" },
    { title: "9 months", id: "9" },
    { title: "10 months", id: "10" },
    { title: "11 months", id: "11" },
    { title: "12 months", id: "12" },
    { title: "13 months", id: "13" },
    { title: "14 months", id: "14" },
    { title: "15 months", id: "15" },
    { title: "16 months", id: "16" },
    { title: "17 months", id: "17" },
    { title: "18 months", id: "18" },
  ];

  const onSubmit = (data: IMembershipSetupGeneralDetailsProps) => {
    let constructedBody: any = null;

    if (membership_id) {
      constructedBody = {
        not_for_sell: params.get("membership_applicable") === "only_new_membership" ? true : false,
        general_details: {
          name: data?.membership_name,
          category: data?.membership_category,
          amount: parseFloat(data?.membership_amount.toString()),
          validity: data?.membership_validity,
          status: data?.status,
          clinic_details: {
            cities: data?.cities.map((city) => ({
              id: city.id,
              name: city.name,
            })),
            clinics: data?.clinics.map((clinic) => ({
              id: clinic.id,
              name: clinic.name,
            })),
          },
        },
      };
    } else {
      constructedBody = {
        general_details: {
          name: data?.membership_name,
          category: data?.membership_category,
          amount: parseFloat(data?.membership_amount.toString()),
          validity: data?.membership_validity,
          status: data?.status,
          clinic_details: {
            cities: data?.cities.map((city) => ({
              id: city.id,
              name: city.name,
            })),
            clinics: data?.clinics.map((clinic) => ({
              id: clinic.id,
              name: clinic.name,
            })),
          },
        },
      };
    }
    if (membership_id) {
      updateMembership(constructedBody, membership_id).then(({ status }) => {
        if (status) {
          navigate(
            `/app/membership-setup/membership-creation?page_step=add_coupon&&membership_id=${membership_id}&&membership_applicable=${params.get("membership_applicable")}`,
          );
        }
      });
    } else {
      membershipGeneralDetailsCreation(constructedBody).then(({ status, message }) => {
        if (status) {
          navigate(`/app/membership-setup/membership-creation?page_step=add_coupon&&membership_id=${message}`);
        }
      });
    }
  };

  useEffect(() => {
    if (membershipDetails) {
      formHook.setValue("membership_name", membershipDetails?.general_details?.name);
      formHook.setValue("membership_category", membershipDetails?.general_details?.category);
      formHook.setValue("membership_amount", membershipDetails?.general_details?.amount);
      formHook.setValue("membership_validity", membershipDetails?.general_details?.validity);
      formHook.setValue("status", membershipDetails?.general_details?.status);
      const lineItems = membershipDetails?.invoice_details?.map((lineItem) => ({
        vms_category_id: lineItem?.vms_category_id,
        vms_category_name: lineItem?.vms_category_name,
        vms_subcategory_name: lineItem?.vms_subcategory_name,
        vms_subcategory_id: lineItem?.vms_subcategory_id,
        membership_line_item_name: lineItem?.line_iteam_name,
        membership_invoice_category: lineItem?.invoice_category,
        membership_invoice_amount: lineItem?.amount,
        tax_preferences: lineItem?.taxable
          ? ("taxable" as TaxDetailsPreferencesTypes)
          : ("nonTaxable" as TaxDetailsPreferencesTypes),
        hsncode: lineItem?.tax_details?.hsn_code || "",
        cgst: lineItem?.tax_details?.tax_rate?.cgst || 0,
        sgst: lineItem?.tax_details?.tax_rate?.sgst || 0,
        igst: lineItem?.tax_details?.tax_rate?.igst || 0,
        ugst: lineItem?.tax_details?.tax_rate?.utgst || 0,
      }));
      formHook.setValue("lineItems", lineItems || []);
      const selectedCities =
        membershipDetails?.general_details?.clinic_details?.cities?.map((city) => ({
          id: city.id,
          name: city.name,
        })) || [];
      formHook.setValue("cities", selectedCities);
      fetchStaffClinicBasedOnCity(selectedCities?.map((city) => city?.id)).then(() => {
        const selectedClinics =
          membershipDetails?.general_details?.clinic_details?.clinics?.map((clinic) => ({
            id: clinic.id,
            name: clinic.name,
          })) || [];
        formHook.setValue("clinics", selectedClinics);
      });
    }
  }, [membershipDetails, formHook, fetchStaffClinicBasedOnCity]);

  const calculateTotal = () => {
    const item = formHook.watch("lineItems").reduce(
      (acc, item) => {
        acc.totalAmount += parseFloat(item.membership_invoice_amount as any) || 0;
        return acc;
      },
      {
        totalAmount: 0,
      },
    );
    return item;
  };

  const handelSubcategoryChange = async (index) => {
    const lineItemsCategory = formHook.watch(`lineItems.${index}.vms_category_id`);

    if (!lineItemsCategory) return;

    try {
      fetchSubCategory(lineItemsCategory).then(({ status, message }) => {
        if (status) {
          const convertedData = message?.map(({ subcategory_id, subcategory_name }) => ({
            title: subcategory_name,
            id: subcategory_id,
          }));
          update(index, {
            ...formHook.getValues(`lineItems.${index}`),
            sub_category_data: convertedData, // Now safely using memoized data
          });
        } else {
          update(index, {
            ...formHook.getValues(`lineItems.${index}`),
            sub_category_data: [], // Now safely using memoized data
          });
        }
      });
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  const membershipCategoryItems = useSelectMenuReducer(membershipCategory, "category_name", "id");

  return (
    <div>
      <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 w-full bg-white px-4 py-3 sm:px-6 lg:px-8">
          <h2 className="text-sm font-semibold">Details</h2>
          <div className="flex items-center">
            <div className="w-2/12">
              <p>
                Name <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <Input
                className="text-xs"
                placeholder="Enter Name"
                register={formHook.register("membership_name", {
                  required: true,
                  validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                })}
                fieldError={formHook.formState.errors.membership_name}
                errorMessages={[
                  { message: "Name is required", type: "required" },
                  { message: "Name cannot be empty or only spaces", type: "validate" },
                ]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formHook?.setValue("membership_name", e.target.value);
                  formHook.clearErrors("membership_name");
                }}
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Category <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableSelectMenu
                errorMessages={[{ message: "Category is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("membership_category", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.membership_category}
                register={formHook.register("membership_category", { required: true })}
                selectItems={membershipCategoryItems}
                placeholder="Select Category"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                withDropDownSearch
                defaultSelected={
                  membershipCategoryItems?.filter((oc) => oc.title === formHook.watch("membership_category"))[0]
                }
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Amount <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <Input
                className="text-xs"
                placeholder="Enter Amount"
                disabled={membership_id && true}
                register={formHook.register("membership_amount", {
                  required: true,
                  ...forNumericWithDecimalAndNotStartFromZero.validations,
                })}
                fieldError={formHook.formState.errors.membership_amount}
                errorMessages={[
                  { message: "Amount is required", type: "required" },
                  forNumericWithDecimalAndNotStartFromZero.errors,
                ]}
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Cities <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const selectedCities = items.map((i) => ({ id: i.id, name: i.title }));
                  formHook.setValue("cities", selectedCities);
                  fetchStaffClinicBasedOnCity(selectedCities.map((city) => city.id));
                  formHook.clearErrors("cities");
                }}
                errorMessages={[{ message: "Cities is required", type: "required" }]}
                fieldError={formHook.formState.errors.cities}
                register={formHook.register("cities", {
                  required: true,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={true}
                placeholder="Select Cities"
                defaultSelected={cityOrgItem.filter((c) => formHook.watch("cities")?.some((city) => city.id === c.id))}
                selectItems={cityOrgItem}
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Clinics <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  const selectedClinics = items.map((i) => ({ id: i.id, name: i.title }));
                  formHook.setValue("clinics", selectedClinics);
                  formHook.clearErrors("clinics");
                }}
                errorMessages={[{ message: "Clinics is required", type: "required" }]}
                fieldError={formHook.formState.errors.clinics}
                register={formHook.register("clinics", {
                  required: true,
                })}
                withSelectAll
                showTooltips={true}
                showTypedErrors={true}
                placeholder="Select Clinics"
                defaultSelected={clinicsOrgItem.filter((c) =>
                  formHook.watch("clinics")?.some((clinic) => clinic.id === c.id),
                )}
                selectItems={clinicsOrgItem}
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>
                Validity <span className="text-red-400">*</span>
              </p>
            </div>
            <div className="w-3/12">
              <SearchableSelectMenu
                errorMessages={[{ message: "Validity is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("membership_validity", item.title);
                  }
                }}
                disabled={membership_id && true}
                fieldError={formHook.formState.errors.membership_validity}
                register={formHook.register("membership_validity", { required: true })}
                selectItems={validityItems}
                placeholder="Select Validity"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={validityItems?.filter((oc) => oc.title === formHook.watch("membership_validity"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>Status</p>
            </div>
            <div className="w-3/6 flex gap-6 items-center">
              <Switch
                checked={formHook.watch("status")}
                onChange={() => {
                  if (formHook.watch("status")) {
                    formHook.setValue("status", false);
                  } else {
                    formHook.setValue("status", true);
                  }
                }}
                className={`${formHook.watch("status") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${formHook.watch("status") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div>
          {/* <div className="flex items-center mt-3">
            <div className="w-2/12">
              <p>Visible On Cart</p>
            </div>
            <div className="w-3/6 flex gap-6 items-center">
              <Switch
                checked={formHook.watch("status")}
                onChange={() => {
                  if (formHook.watch("status")) {
                    formHook.setValue("status", false);
                  } else {
                    formHook.setValue("status", true);
                  }
                }}
                className={`${formHook.watch("status") ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
          relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${formHook.watch("status") ? "translate-x-8" : "translate-x-0"}
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>
          </div> */}
        </div>
        {/* <div className="mt-3 px-4 py-3 bg-white">
          <h2 className="text-sm font-semibold sm:px-6 lg:px-3">Invoice Structure</h2>
          {fields?.map((item, index) => (
            <div key={item.id} className="relative mt-5 flex flex-col gap-4 w-full border border-gray-200 p-3">
              {index !== 0 && !membership_id && (
                <div className="absolute top-2 right-2 cursor-pointer" onClick={() => deleteLineItem(index)}>
                  <IoTrashOutline color="red" size={20} />
                </div>
              )}
              <div className="flex items-center mt-3">
                <div className="w-2/12">
                  <p>
                    Line Item Name <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  <Input
                    key={item.id}
                    disabled={membership_id && true}
                    className="text-xs"
                    placeholder="Enter Name"
                    register={formHook.register(`lineItems.${index}.membership_line_item_name`, {
                      required: true,
                      validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                    })}
                    fieldError={formHook.formState.errors.lineItems?.[index]?.membership_line_item_name}
                    errorMessages={[
                      { message: "Line Item Name is required", type: "required" },
                      { message: "Name cannot be empty or only spaces", type: "validate" },
                    ]}
                  />
                </div>
              </div>
              <div className="flex items-center mt-3">
                <div className="w-2/12">
                  <p>
                    Invoice Category <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  <SearchableSelectMenu
                    disabled={membership_id && true}
                    errorMessages={[{ message: "Invoice Category is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue(`lineItems.${index}.membership_invoice_category`, item.title);
                      }
                    }}
                    fieldError={formHook.formState.errors.lineItems?.[index]?.membership_invoice_category}
                    register={formHook.register(`lineItems.${index}.membership_invoice_category`, { required: true })}
                    selectItems={membershipCategoryOptions}
                    placeholder="Select Category"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    withDropDownSearch
                    addAction={{
                      title: `+ ${"Add"}`,
                      callBack: (item: string) => handelCreateItems(item),
                    }}
                    defaultSelected={
                      membershipCategoryOptions?.filter(
                        (oc) => oc.title === formHook.watch(`lineItems.${index}.membership_invoice_category`),
                      )[0]
                    }
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-2/12">
                  <p>
                    VMS Category <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  {membership_id ? (
                    <Input
                      key={item.id}
                      disabled={membership_id && true}
                      className="text-xs"
                      placeholder="Enter Name"
                      register={formHook.register(`lineItems.${index}.vms_category_name`, {
                        required: true,
                        validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                      })}
                      fieldError={formHook.formState.errors.lineItems?.[index]?.vms_category_name}
                      errorMessages={[
                        { message: "Line Item Name is required", type: "required" },
                        { message: "Name cannot be empty or only spaces", type: "validate" },
                      ]}
                    />
                  ) : (
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Category is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue(`lineItems.${index}.vms_category_id`, item.id);
                          formHook.setValue(`lineItems.${index}.vms_category_name`, item.title);
                          handelSubcategoryChange(index);
                        }
                      }}
                      fieldError={formHook.formState.errors.lineItems?.[index]?.vms_category_id}
                      register={formHook.register(`lineItems.${index}.vms_category_id`, { required: true })}
                      selectItems={couponCategoryItem}
                      placeholder="Select Category"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      // addAction={{
                      //   title: `+ ${"Add"} `,
                      //   callBack: (item: string) => handelCreateItems(item),
                      // }}
                      defaultSelected={
                        couponCategoryItem?.filter(
                          (oc) => oc.id === formHook.watch(`lineItems.${index}.vms_category_id`),
                        )[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800 "
                      containerClassName="w-full"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-2/12">
                  <p>
                    VMS Sub-Category <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  {membership_id ? (
                    <Input
                      key={item.id}
                      disabled={membership_id && true}
                      className="text-xs"
                      placeholder="Enter Name"
                      register={formHook.register(`lineItems.${index}.vms_subcategory_name`, {
                        required: true,
                        validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                      })}
                      fieldError={formHook.formState.errors.lineItems?.[index]?.vms_subcategory_name}
                      errorMessages={[
                        { message: "Line Item Name is required", type: "required" },
                        { message: "Name cannot be empty or only spaces", type: "validate" },
                      ]}
                    />
                  ) : (
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Sub-category is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue(`lineItems.${index}.vms_subcategory_id`, item.id);
                          formHook.setValue(`lineItems.${index}.vms_subcategory_name`, item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.lineItems?.[index]?.vms_subcategory_id}
                      register={formHook.register(`lineItems.${index}.vms_subcategory_id`, { required: true })}
                      selectItems={formHook.watch(`lineItems.${index}.sub_category_data`)}
                      placeholder="Select Sub-Category"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      // addAction={{
                      //   title: `+ ${"Add"} `,
                      //   callBack: (item: string) => handleCreateSubCategory(item),
                      // }}
                      defaultSelected={
                        formHook
                          .watch(`lineItems.${index}.sub_category_data`)
                          ?.filter((oc) => oc.id === formHook.watch(`lineItems.${index}.vms_subcategory_id`))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800 "
                      containerClassName="w-full"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center mt-3">
                <div className="w-2/12">
                  <p>
                    Amount <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  <Input
                    className="text-xs"
                    disabled={membership_id && true}
                    placeholder="Enter Amount"
                    register={formHook.register(`lineItems.${index}.membership_invoice_amount`, {
                      required: true,
                      ...forNumericWithDecimalAndNotStartFromZero.validations,
                    })}
                    fieldError={formHook.formState.errors.lineItems?.[index]?.membership_invoice_amount}
                    errorMessages={[
                      { message: "Amount is required", type: "required" },
                      forNumericWithDecimalAndNotStartFromZero.errors,
                    ]}
                  />
                </div>
              </div>
              <div className="flex items-center mt-3">
                <div className="w-2/12">
                  <p>
                    Tax Preference <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-2/5 flex items-center gap-6">
                  <RadioGroupComponent
                    disabled={membership_id && true}
                    alignment="horizontal"
                    errorMessages={[]}
                    fieldError={formHook.formState.errors.lineItems?.[index]?.tax_preferences}
                    register={formHook.register(`lineItems.${index}.tax_preferences`)}
                    groupOptions={[
                      { id: "taxable", title: "Taxable", colorClassName: "text-green-500" },
                      { id: "nonTaxable", title: "Non Taxable", colorClassName: "text-red-500" },
                    ]}
                    defaultSelectedId={formHook.watch(`lineItems.${index}.tax_preferences`)}
                    onRadioChange={(optionId: string) => {
                      formHook.setValue(`lineItems.${index}.tax_preferences`, optionId as "taxable" | "nonTaxable");
                    }}
                    radioOptionClassName="text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center mt-3">
                <div className="w-2/12">
                  <p>
                    HSN Code <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  <SearchableSelectMenu
                    disabled={
                      formHook.watch(`lineItems.${index}.tax_preferences`) === "nonTaxable" || (membership_id && true)
                    }
                    errorMessages={[{ message: "HSN Code is required", type: "required" }]}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue(`lineItems.${index}.hsncode`, item.title);
                        formHook.clearErrors(`lineItems.${index}.hsncode`);
                        const selectedTaxData = taxList.find((taxItem) => taxItem.hsn_code === item.title);
                        if (selectedTaxData) {
                          formHook.setValue(`lineItems.${index}.cgst`, selectedTaxData.cgst_percentage || 0);
                          formHook.setValue(`lineItems.${index}.sgst`, selectedTaxData.sgst_percentage || 0);
                          formHook.setValue(`lineItems.${index}.igst`, selectedTaxData.igst_percentage || 0);
                          formHook.setValue(`lineItems.${index}.ugst`, selectedTaxData.ugst_percentage || 0);
                        }
                      }
                    }}
                    fieldError={formHook.formState.errors.lineItems?.[index]?.hsncode}
                    register={formHook.register(`lineItems.${index}.hsncode`, {
                      required:
                        formHook.watch(`lineItems.${index}.tax_preferences`) === "taxable" ||
                        formHook.watch(`lineItems.${index}.tax_preferences`) == undefined
                          ? true
                          : false,
                    })}
                    selectItems={taxListItems}
                    placeholder="Select HSN Code"
                    showTooltips={true}
                    showTypedErrors={true}
                    showDropdownIcon={true}
                    defaultSelected={
                      taxListItems?.filter((oc) => oc.title === formHook.watch(`lineItems.${index}.hsncode`))[0]
                    }
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full"
                  />
                </div>
              </div>
              <div className="flex items-center mt-3">
                <div className="w-2/12">
                  <p>Tax Rate</p>
                </div>
                <div className="w-2/4 flex flex-col gap-4">
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2">
                      <p className="w-22">CGST (%) :</p>
                      <Input
                        className="text-xs w-28"
                        placeholder="Enter CGST"
                        disabled={true}
                        register={formHook.register(`lineItems.${index}.cgst`, { required: false })}
                        fieldError={formHook.formState.errors.lineItems?.[index]?.cgst}
                        errorMessages={[]}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="w-22">SGST (%) :</p>
                      <Input
                        className="text-xs w-28"
                        disabled={true}
                        placeholder="Enter SGST"
                        register={formHook.register(`lineItems.${index}.sgst`, { required: false })}
                        fieldError={formHook.formState.errors.lineItems?.[index]?.sgst}
                        errorMessages={[]}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2">
                      <p className="w-22">IGST (%) :</p>
                      <Input
                        className="text-xs w-28"
                        disabled={true}
                        placeholder="Enter IGST"
                        register={formHook.register(`lineItems.${index}.igst`, { required: false })}
                        fieldError={formHook.formState.errors.lineItems?.[index]?.igst}
                        errorMessages={[]}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="w-22">UTGST (%) :</p>
                      <Input
                        className="text-xs w-28"
                        disabled={true}
                        placeholder="Enter UTGST"
                        register={formHook.register(`lineItems.${index}.ugst`, { required: false })}
                        fieldError={formHook.formState.errors.lineItems?.[index]?.ugst}
                        errorMessages={[]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="text-sm font-semibold pt-3 ">Total Amount : {calculateTotal().totalAmount}</div>
          {!membership_id && (
            <div className="flex gap-3 mt-3 items-center cursor-pointer" onClick={addLineItem}>
              <IoIosAddCircle color="rgba(0, 104, 255, 1)" size={22} />
              <p className="text-[#0267fe] text-sm font-medium">Add Another Line Item</p>
            </div>
          )}
        </div> */}
        <div className="right-5 bottom-0  flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white ">
          <p className="col-span-6"></p>
          <button
            type="button"
            onClick={() => handelBackStepper()}
            className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#b1b5bb] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#193A6A] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
          >
            Save & Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default MembershipDetailsComponent;
